import { Link, useParams } from "@tanstack/react-router";
import { Badge, EmptyState, Fact, Facts, PageHeader, Section, Table, TBody, TD, TDNum, TFoot, TH, THead, TR, formatCount, statusTone } from "@ghezaa/ui";
import { formatDateTime, recallListSearch } from "@ghezaa/shell";
import { Storefront } from "@phosphor-icons/react";
import type { CustomerFigures } from "@/api/client";
import { useCustomer, useCustomerFigures } from "@/api/queries";
import { blockedWord, day, inCurrency, money, monthName, percent } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";
import { CUSTOMERS_LIST } from "./customers";

/** One customer as BC has it: who it is, whose book it is in, what it owes, what it bought month by month and item by item, and its outlets. */
export function CustomerPage() {
  const market = useCurrentMarket();
  const { no } = useParams({ strict: false }) as { no: string };
  const customer = useCustomer(market.code, no);
  const figures = useCustomerFigures(market.code, no);
  const back = (
    <Link from="/$market" to="/$market/customers" search={{ ...recallListSearch(CUSTOMERS_LIST), last: no } as never} className="hover:underline">
      Customers ›
    </Link>
  );

  if (customer.isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow={back} title="Customer" />
        <EmptyState icon={Storefront} title="No such customer here" description="It is not in BC's last read, or not in a book you see." />
      </div>
    );
  }
  if (!customer.data) return <div className="h-40 bg-surface-2" aria-busy="true" />;

  const c = customer.data;
  const f = figures.data;
  const blocked = blockedWord(c.blocked);
  const salesperson = c.salesperson;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow={back}
        title={c.name || c.no}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{c.no}</span>
            {c.searchName && <span>{c.searchName}</span>}
            {blocked && <Badge tone={statusTone("blocked")}>{blocked}</Badge>}
            {f?.silent && <Badge tone={statusTone("silent")}>Silent</Badge>}
          </span>
        }
      />

      <Facts>
        <Fact label="Salesperson" sub={salesperson ? (salesperson.person ?? "Not linked to anyone in Sales") : undefined}>
          {salesperson ? salesperson.name || salesperson.code : <span className="text-ink-3">None in BC</span>}
        </Fact>
        <Fact label={inCurrency("Balance", market.currency)} sub={c.creditLimit ? `Credit limit ${money(c.creditLimit)}` : "No credit limit"}>
          <span className="tabular-nums">{money(c.balance)}</span>
        </Fact>
        <Fact label={inCurrency("Overdue", market.currency)}>
          <span className="tabular-nums">{money(c.overdue)}</span>
        </Fact>
        <Fact label={inCurrency("Sales to date", market.currency)} sub="Before VAT, since the customer began">
          <span className="tabular-nums">{money(c.salesToDate)}</span>
        </Fact>
        <Fact label="Last invoice" sub={f?.usualGapDays ? (f.usualGapDays === 1 ? "Usually every day" : `Usually every ${f.usualGapDays} days`) : f?.firstInvoiceOn ? `First ${day(f.firstInvoiceOn)}` : undefined}>
          {f?.lastInvoiceOn ? <span className="tabular-nums">{day(f.lastInvoiceOn)}</span> : <span className="text-ink-3">None</span>}
        </Fact>
        <Fact label="Payment terms" sub={c.postingGroup || undefined}>
          {c.paymentTerms || <span className="text-ink-3">None</span>}
        </Fact>
        <Fact label="City" sub={[c.phone, c.email].filter(Boolean).join(" · ") || undefined}>
          {c.city || <span className="text-ink-3">None</span>}
        </Fact>
      </Facts>

      {f && <MonthsSection figures={f} currency={market.currency} />}
      {f && <ItemsSection figures={f} currency={market.currency} />}

      <Section n="03" title="Outlets" note={c.outlets.length === 0 ? "BC has no ship-to for this customer: it is delivered at its own address." : undefined} wide>
        {c.outlets.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>Outlet</TH>
                <TH from="md">Code</TH>
                <TH from="md">City</TH>
                <TH from="lg">Phone</TH>
              </TR>
            </THead>
            <TBody>
              {c.outlets.map((o) => (
                <TR key={o.code}>
                  <TD className="w-full max-w-0">
                    <span className="block truncate">{o.name}</span>
                    <span className="block truncate text-xs text-ink-3 md:hidden">
                      <span className="font-mono">{o.code}</span>
                      {o.city ? ` · ${o.city}` : ""}
                    </span>
                  </TD>
                  <TD from="md" className="whitespace-nowrap font-mono text-xs">{o.code}</TD>
                  <TD from="md" className="whitespace-nowrap">{o.city}</TD>
                  <TD from="lg" className="whitespace-nowrap tabular-nums">{o.phone}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Section>

      <p className="text-xs text-ink-3">From Business Central, read {formatDateTime(c.readAt)}.</p>
    </div>
  );
}

/** Its last twelve months, this one first; the total is the twelve months'. */
function MonthsSection({ figures, currency }: { figures: CustomerFigures; currency: string }) {
  const months = figures.months;
  const total = months.reduce(
    (sum, m) => ({ revenue: sum.revenue + m.totals.revenue, profit: sum.profit + m.totals.grossProfit, invoices: sum.invoices + m.totals.invoices, lines: sum.lines + m.totals.lines }),
    { revenue: 0, profit: 0, invoices: 0, lines: 0 },
  );
  const largest = Math.max(...months.map((m) => m.totals.revenue), 0);
  if (total.invoices === 0 && total.revenue === 0) {
    return (
      <Section n="01" title="Last 12 months" wide>
        <p className="text-sm text-ink-3">No invoice in the last twelve months.</p>
      </Section>
    );
  }
  return (
    <Section n="01" title="Last 12 months" note="Before VAT, credit memos taken off." wide>
      <Table density="compact">
        <THead>
          <TR>
            <TH>Month</TH>
            <TH bar className="text-end whitespace-nowrap">{inCurrency("Revenue", currency)}</TH>
            <TH from="sm" className="text-end">Margin</TH>
            <TH from="md" className="text-end">Invoices</TH>
            <TH from="md" className="text-end">Lines</TH>
          </TR>
        </THead>
        <TBody>
          {months.map((m) => (
            <TR key={m.month}>
              <TD className="w-full whitespace-nowrap">{monthName(m.month)}</TD>
              <TDNum bar={largest > 0 ? Math.max(0, m.totals.revenue) / largest : 0}>{m.totals.revenue ? money(m.totals.revenue) : ""}</TDNum>
              <TDNum from="sm">{m.totals.revenue ? percent(m.totals.margin) : ""}</TDNum>
              <TDNum from="md">{m.totals.invoices || ""}</TDNum>
              <TDNum from="md">{m.totals.lines || ""}</TDNum>
            </TR>
          ))}
        </TBody>
        <TFoot>
          <TR>
            <TD>Total</TD>
            <TDNum bar={null}>{money(total.revenue)}</TDNum>
            <TDNum from="sm">{total.revenue ? percent(total.profit / total.revenue) : ""}</TDNum>
            <TDNum from="md">{formatCount(total.invoices)}</TDNum>
            <TDNum from="md">{formatCount(total.lines)}</TDNum>
          </TR>
        </TFoot>
      </Table>
    </Section>
  );
}

/** What it buys now against before: the last 90 days and the 90 before them. A line it stopped buying shows with nothing now. */
function ItemsSection({ figures, currency }: { figures: CustomerFigures; currency: string }) {
  if (figures.items.length === 0) return null;
  return (
    <Section n="02" title="Items" note={`The last 90 days against the 90 before, from ${day(figures.earlier.from)}.`} wide>
      <Table density="compact">
        <THead>
          <TR>
            <TH>Item</TH>
            <TH from="md" className="text-end">Quantity</TH>
            <TH className="text-end whitespace-nowrap">{inCurrency("Last 90 days", currency)}</TH>
            <TH from="sm" className="text-end whitespace-nowrap">{inCurrency("90 before", currency)}</TH>
          </TR>
        </THead>
        <TBody>
          {figures.items.map((i) => (
            <TR key={i.no}>
              <TD className="w-full max-w-0">
                <span className="block truncate">{i.name || i.no}</span>
                <span className="block text-xs text-ink-3">
                  <span className="font-mono">{i.no}</span>
                  {i.revenue === 0 && i.revenueBefore > 0 ? " · not bought lately" : ""}
                </span>
              </TD>
              <TDNum from="md">{i.quantity ? formatCount(i.quantity) : ""}</TDNum>
              <TDNum>{i.revenue ? money(i.revenue) : ""}</TDNum>
              <TDNum from="sm">{i.revenueBefore ? money(i.revenueBefore) : ""}</TDNum>
            </TR>
          ))}
        </TBody>
      </Table>
    </Section>
  );
}
